import { v4 as UUIDv4 } from 'uuid';
import * as Cookies from 'js-cookie';
import { LogService } from '@landr/core';
import { PageDataType } from 'templates/Page';
import { SliceContext } from 'components/Slices/types';
import { log } from '../log';
import { EventId } from '../enum/eventId';
import { getEnglishUID } from '../helpers/getEnglishUID';

export const AMPLITUDE_COOKIE_NAME = 'amplitudeDeviceId';
export const AMPLITUDE_COOKIE_SETTINGS = {
    path: '/',
    domain: process.env.COOKIE_DOMAIN,
};
export const APPLICATION_NAME = 'GUEST SITE';

export interface SegmentWindow extends Window {
    analytics?: any;
}

export enum TransformMethods {
    JOIN,
    UPPERCASE,
    LOWERCASE,
}

type CommonParameters = {
    'L - Application Name': string;
};

type CommonPageParameters = {
    'L - Page': string;
    'L - Page Language': string;
    'L - Count of Slices': number;
};

export class AbstractSegmentService {
    public commonPageParams: CommonPageParameters = {} as CommonPageParameters;
    private window: SegmentWindow | undefined;
    private disabled: boolean = process.env.SEGMENT_DISABLED === 'true';
    private commonParams: CommonParameters = {
        'L - Application Name': APPLICATION_NAME,
    };
    private log: LogService;

    constructor() {
        this.log = log.getInstance('SegmentAbstract');
        if (typeof window !== 'undefined') {
            this.window = window;
        }
    }

    /**
     * This method is based on the Segment snippet (and converted to Typescript).
     * It asynchronously loads Segment, and creates a queue used until Segment is ready.
     */
    load(): void {
        if (this.disabled) {
            return;
        }

        if (typeof this.window !== 'undefined') {
            // Create a queue, but don't obliterate an existing one!
            const analytics = (this.window.analytics = this.window.analytics || []);

            // If the real analytics.js is already on the page return.
            if (analytics.initialize) {
                return;
            }

            // If the snippet was invoked already, show an error.
            if (analytics.invoked) {
                this.log.warn('Segment analytics included twice', EventId.SegmentAnalyticsFailed);
                return;
            }

            // Invoked flag, to make sure the snippet is never invoked twice.
            analytics.invoked = true;

            // A list of the methods in Segment's Analytics.js to stub.
            analytics.methods = [
                'trackSubmit',
                'trackClick',
                'trackLink',
                'trackForm',
                'pageview',
                'identify',
                'reset',
                'group',
                'track',
                'ready',
                'alias',
                'debug',
                'page',
                'once',
                'off',
                'on',
            ];

            // Define a factory to create stubs. These are placeholders
            // for methods in Analytics.js so that you never have to wait
            // for it to load to actually record data. The `method` is
            // stored as the first argument, so we can replay the data.
            analytics.factory = (method: string) => {
                return function() {
                    const args = Array.prototype.slice.call(arguments);
                    args.unshift(method);
                    analytics.push(args);
                    return analytics;
                };
            };

            // For each of our methods, generate a queueing stub.
            for (const key of analytics.methods) {
                analytics[key] = analytics.factory(key);
            }

            // Define a method to load Analytics.js from our CDN,
            // and that will be sure to only ever load it once.
            analytics.load = function(key: string, options?: any) {
                // Create an async script element based on your key.
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.async = true;
                script.src = `//cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`;

                // Insert our script next to the first script element.
                const first = document.getElementsByTagName('script')[0];
                if (first && first.parentNode) {
                    first.parentNode.insertBefore(script, first);
                }
                analytics._loadOptions = options;
            };

            // Add a version to keep track of what's in the wild.
            analytics.SNIPPET_VERSION = '4.1.0';

            // Load Analytics.js with your key, which will automatically
            // load the tools you've enabled for your account. Boosh!
            analytics.load(process.env.GATSBY_SEGMENT_WRITE_KEY);

            this.window.analytics.ready(() => {
                this.log.info('Segment ready');
                if (typeof this.window !== 'undefined') {
                    this.window.analytics.user().anonymousId(this.getDeviceId());
                }
            });
        }
    }

    reset(): void {
        if (this.disabled) {
            return;
        }

        if (typeof this.window !== 'undefined') {
            this.window.analytics.reset();
            if (this.window.analytics.user) {
                this.window.analytics.user().anonymousId(this.getDeviceId(true));
            }
        }
    }

    page(page: PageDataType): void {
        if (this.disabled) {
            return;
        }

        const pageName = getEnglishUID(page);
        this.commonPageParams = {
            'L - Page': pageName,
            'L - Page Language': page.lang.substring(0, 2),
            'L - Count of Slices': page.data.body ? page.data.body.length : 0,
        };

        if (typeof this.window !== 'undefined') {
            this.window.analytics.page(pageName, {
                ...this.commonParams,
                ...this.commonPageParams,
                'L - Last Publication Date': page.last_publication_date,
                'L - First Publication Date': page.first_publication_date,
            });
        }
    }

    track<T extends object>(
        name: string,
        category: string,
        gaLabelProperty: keyof T | null,
        gaValueProperty: keyof T | null,
        params: T,
        sliceContext: SliceContext = {} as SliceContext,
    ): Promise<void> {
        return new Promise(resolve => {
            if (this.disabled) {
                resolve();
                return;
            }

            if (typeof this.window !== 'undefined') {
                const gaLabel = gaLabelProperty ? params[gaLabelProperty] : undefined;
                const gaValue = gaValueProperty ? params[gaValueProperty] : undefined;

                this.window.analytics.track(
                    name,
                    {
                        ...this.commonParams,
                        ...this.commonPageParams,
                        ...(params as object),
                        'Event Category': category,
                        'L - Slice Type': sliceContext.type,
                        'L - Slice Position': sliceContext.position,
                        label: gaLabel,
                        value: gaValue,
                    },
                    {},
                    resolve,
                );
            }
        });
    }

    transform(method: TransformMethods, value: string | string[]): any {
        let result = value;

        switch (method) {
            case TransformMethods.JOIN:
                result = Array.isArray(result) ? result.join(', ') : result;
                break;
            case TransformMethods.LOWERCASE:
                result = Array.isArray(result)
                    ? result.map(r => r.toLowerCase())
                    : result.toLowerCase();
                break;
            case TransformMethods.UPPERCASE:
                result = Array.isArray(result)
                    ? result.map(r => r.toUpperCase())
                    : result.toUpperCase();
                break;
            default:
                break;
        }

        return result;
    }

    /**
     * Generates a deviceId for Segment.
     * If set, we use `AMPLITUDE_COOKIE_NAME` cookie because WebApp, Blog and
     * Samples use it for segment as well. Cookie is valid 5 years.
     * @param forceRefresh true to regenerate an deviceID
     */
    private getDeviceId(forceRefresh = false): string {
        /* eslint-disable-next-line new-cap */
        const deviceId = forceRefresh ? UUIDv4() : Cookies.get(AMPLITUDE_COOKIE_NAME) || UUIDv4();

        Cookies.set(AMPLITUDE_COOKIE_NAME, deviceId, {
            secure: process.env.SECURE_COOKIES === 'true',
            expires: 5 * 365,
            ...AMPLITUDE_COOKIE_SETTINGS,
        });

        return deviceId;
    }
}
